import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import {
  AccessComponentEnum,
  AccessLevelEnum,
  AiFieldDetectionStatusEnum,
  AiMediaKindEnum,
  useEdmDocumentRenewalReminderTemplateQuery,
} from 'graphql-common';
import BreadCrumbs from '@lib/components/BreadCrumbs/BreadCrumbs';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import PageHeader from '@lib/layouts/DashboardLayout/PageHeader/PageHeader';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import DashboardContentMaxWidth from '@lib/layouts/DashboardLayout/DashboardContentMaxWidth/DashboardContentMaxWidth';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import Form from '@lib/components/ReactHookForm/FormContainer';
import { FormRef } from '@lib/components/ReactHookForm/types';
import Modal from '@lib/components/Modal/Modal';
import useModalManagement from '@lib/hooks/useModalManagement';
import DocumentFormFieldNames from '@lib/enums/fieldNames/documentFormFieldNames';
import useAiBudgetStatus from 'components/AiAssistant/hooks/useAiBudgetStatus';
import useAiIndexableMediaKinds from 'components/AiAssistant/hooks/useAiIndexableMediaKinds';
import AssetsModalTableMultiselect from 'components/AssetsModalTableMultiselect/AssetsModalTableMultiselect';
import SiteAreasModalTableMultiselect from 'components/SiteAreasModalTableMultiselect/SiteAreasModalTableMultiselect';
import TasksTableMultiselect from 'components/TasksTableMultiselect/TasksTableMultiselect';
import FormTemplatesModalTableMultiselect from 'components/FormTemplatesModalTableMultiselect';
import { AiFieldGenerationProvider } from 'routes/Documentation/components/AiDetection/AiFieldGenerationContext';
import useAiFieldGeneration from 'routes/Documentation/DocumentForm/utils/useAiFieldGeneration';
import { getBreadCrumbsDataByKey } from 'utils/breadcrumbs';
import useDocumentDetectionRefetch from 'routes/Documentation/utils/useDocumentDetectionRefetch';
import useAiDetectionDismiss from 'routes/Documentation/utils/useAiDetectionDismiss';
import { getGraphQLErrors } from '@lib/utils/graphQLErrors';
import DocumentFormActions from './components/DocumentFormActions';
import getDocumentFormFields from './utils/getDocumentFormFields';
import getDefaultValuesFromQueryData from './utils/getDefaultValuesFromQueryData';
import useDocumentFormActions from './utils/useDocumentFormActions';
import useAddCategoryModal from './utils/useAddCategoryModal';
import DocumentCategoryForm from '../DocumentCategories/DocumentCategoryForm';

const SELECT_EQUIPMENT_MODAL = 'select-equipment';
const SELECT_AREAS_MODAL = 'select-areas';
const SELECT_TASKS_MODAL = 'select-tasks';
const SELECT_FORM_TEMPLATES_MODAL = 'select-form-templates';
const DOCUMENT_MODALS = [
  SELECT_EQUIPMENT_MODAL,
  SELECT_AREAS_MODAL,
  SELECT_TASKS_MODAL,
  SELECT_FORM_TEMPLATES_MODAL,
];

export default function DocumentForm({ isEdit }: { isEdit?: boolean }) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const formRef = useRef<FormRef>(undefined);
  const useModalManagementResult = useModalManagement(DOCUMENT_MODALS);
  const aiGeneration = useAiFieldGeneration();

  const {
    getUserAccessByCompany,
    getUserAccessByComponent,
    isFullAdmin,
  }: ContextProps = useOutletContext();
  const isPlanLocked =
    getUserAccessByCompany(AccessComponentEnum.DocumentationManagement) ===
    AccessLevelEnum.LimitedRead;
  // Renewal-reminder feature is locked when EDM is restricted at either
  // company plan or user-role level; skip its template query in both cases.
  const isDocumentationLocked =
    isPlanLocked ||
    getUserAccessByComponent(AccessComponentEnum.DocumentationManagement) ===
      AccessLevelEnum.LimitedRead;
  const hasCategoriesAccess =
    getUserAccessByComponent(
      AccessComponentEnum.DocumentationCategoriesManagement,
    ) !== AccessLevelEnum.LimitedRead;
  // Inline "Create new" category requires write access — Read is view-only.
  const canManageCategories =
    getUserAccessByComponent(
      AccessComponentEnum.DocumentationCategoriesManagement,
    ) === AccessLevelEnum.Write;
  const hasSitesAccess =
    getUserAccessByComponent(AccessComponentEnum.SitesManagement) !==
    AccessLevelEnum.LimitedRead;
  const hasAssetsAccess =
    getUserAccessByComponent(AccessComponentEnum.AssetsManagement) !==
    AccessLevelEnum.LimitedRead;
  const hasTasksAccess =
    getUserAccessByComponent(AccessComponentEnum.TasksManagement) !==
    AccessLevelEnum.LimitedRead;
  const hasUsersAccess =
    getUserAccessByComponent(AccessComponentEnum.UsersManagement) !==
    AccessLevelEnum.LimitedRead;
  // AI auto-fill of document fields requires the AiAssistance module on the
  // company plan (Write) AND the current user/team isn't individually
  // restricted from it.
  const isAiEnabled =
    getUserAccessByCompany(AccessComponentEnum.AiAssistance) ===
      AccessLevelEnum.Write &&
    getUserAccessByComponent(AccessComponentEnum.AiAssistance) ===
      AccessLevelEnum.Write;
  // AI field-detection state (aiDetectionStatus / suggestedCodes) is gated on
  // the BE the same way as the view page: requesting it without both
  // AiAssistance (Write) and an unlocked plan nullifies the whole resolver.
  const isAiDetectionEnabled = isAiEnabled && !isPlanLocked;
  // Remaining AI credits shown under each assist row. Kept current by the
  // dashboard's shared budget (live subscription + a re-read whenever a
  // generation settles), so nothing to refresh from here. Hidden when AI is off
  // or no cap is configured.
  const { credits: aiCredits } = useAiBudgetStatus(isAiEnabled);
  // Media kinds the company's AI indexing pipeline can actually turn into
  // searchable text (region-derived) — some regions can't index video, so the
  // assist affordance must stay hidden on a video attachment even though AI is
  // otherwise enabled.
  const isVideoAiIndexingSupported = useAiIndexableMediaKinds(
    isAiEnabled,
  ).includes(AiMediaKindEnum.Video);

  const {
    approverLoadOptions,
    areasLoadOptions,
    categoriesLoadOptions,
    data,
    equipmentLoadOptions,
    firstLoading,
    formTemplatesLoadOptions,
    graphQLErrors,
    linkedEntityIds,
    loading,
    onCancel,
    onCreateTag,
    onSubmitForm,
    prefillData,
    refetch,
    sectorsLoadOptions,
    sitesLoadOptions,
    tagsLoadOptions,
    tasksLoadOptions,
  } = useDocumentFormActions({
    isEdit,
    isPlanLocked,
    isAiDetectionEnabled,
    hasCategoriesAccess,
    hasSitesAccess,
    hasAssetsAccess,
    hasTasksAccess,
    hasUsersAccess,
  });

  // Edit-mode generation source: the document's current saved version.
  const documentationVersionId = data?.currentVersion?.id;

  // Any field still QUEUED/DETECTING from an import-time (or in-flight)
  // detection run — keep the form live-updating until it settles (including the
  // terminal CANCELLED status the BE writes on cancel), exactly like the view
  // page does.
  const isDetecting =
    isAiDetectionEnabled &&
    (data?.aiDetectionStatus || []).some(
      (entry) =>
        entry.status === AiFieldDetectionStatusEnum.Queued ||
        entry.status === AiFieldDetectionStatusEnum.Detecting,
    );
  useDocumentDetectionRefetch({ isDetecting, refetch });

  const { isDismissed: isAiFieldDismissed, dismiss: dismissAiField } =
    useAiDetectionDismiss(data?.id);

  const { data: reminderTemplateData } =
    useEdmDocumentRenewalReminderTemplateQuery({
      fetchPolicy: 'cache-and-network',
      skip: isDocumentationLocked,
    });
  const renewalReminderTemplate = reminderTemplateData?.data ?? null;
  const hasPriorReminderValues = !!(
    data?.renewalDeadlineDate || data?.renewalRepetition
  );

  const {
    isCategoryModalOpen,
    categoryCacheKey,
    openAddCategoryModal,
    closeAddCategoryModal,
    onSubmitAddCategory,
    categoryCreateMutationResult,
  } = useAddCategoryModal({ formRef });

  // Modal open handlers
  const openEquipmentModal = useCallback(() => {
    useModalManagementResult.openModal(SELECT_EQUIPMENT_MODAL);
  }, [useModalManagementResult]);

  const openAreasModal = useCallback(() => {
    useModalManagementResult.openModal(SELECT_AREAS_MODAL);
  }, [useModalManagementResult]);

  const openTasksModal = useCallback(() => {
    useModalManagementResult.openModal(SELECT_TASKS_MODAL);
  }, [useModalManagementResult]);

  const openFormTemplatesModal = useCallback(() => {
    useModalManagementResult.openModal(SELECT_FORM_TEMPLATES_MODAL);
  }, [useModalManagementResult]);

  // Modal close handlers
  const onCloseEquipmentModal = useCallback(() => {
    useModalManagementResult.closeModal(SELECT_EQUIPMENT_MODAL);
  }, [useModalManagementResult]);

  const onCloseAreasModal = useCallback(() => {
    useModalManagementResult.closeModal(SELECT_AREAS_MODAL);
  }, [useModalManagementResult]);

  const onCloseTasksModal = useCallback(() => {
    useModalManagementResult.closeModal(SELECT_TASKS_MODAL);
  }, [useModalManagementResult]);

  const onCloseFormTemplatesModal = useCallback(() => {
    useModalManagementResult.closeModal(SELECT_FORM_TEMPLATES_MODAL);
  }, [useModalManagementResult]);

  // Modal select handlers
  const onEquipmentSelect = useCallback(
    (values: string[], visibilityMap?: Record<string, boolean>) => {
      if (formRef.current) {
        formRef.current.setValue(
          DocumentFormFieldNames.Equipment,
          values.length
            ? values.map((id) => {
                const visibility = visibilityMap?.[id];
                return visibility === undefined
                  ? { value: id }
                  : { value: id, visibleViaQr: visibility };
              })
            : [],
          { shouldDirty: true, shouldTouch: true, shouldValidate: true },
        );
      }
    },
    [],
  );

  const onAreasSelect = useCallback(
    (values: string[], visibilityMap?: Record<string, boolean>) => {
      if (formRef.current) {
        formRef.current.setValue(
          DocumentFormFieldNames.Areas,
          values.length
            ? values.map((id) => {
                const visibility = visibilityMap?.[id];
                return visibility === undefined
                  ? { value: id }
                  : { value: id, visibleViaQr: visibility };
              })
            : [],
          { shouldDirty: true, shouldTouch: true, shouldValidate: true },
        );
      }
    },
    [],
  );

  const onTasksSelect = useCallback((values: string[]) => {
    if (formRef.current) {
      formRef.current.setValue(
        DocumentFormFieldNames.Tasks,
        values.length ? values.map((id) => ({ value: id })) : [],
        { shouldDirty: true, shouldTouch: true, shouldValidate: true },
      );
    }
  }, []);

  const onFormTemplatesSelect = useCallback((values: string[]) => {
    if (formRef.current) {
      formRef.current.setValue(
        DocumentFormFieldNames.FormTemplates,
        values.length ? values.map((id) => ({ value: id })) : [],
        { shouldDirty: true, shouldTouch: true, shouldValidate: true },
      );
    }
  }, []);

  const formFields = useMemo(
    () =>
      getDocumentFormFields({
        t,
        isEdit,
        approverLoadOptions,
        areasLoadOptions,
        categoriesLoadOptions,
        equipmentLoadOptions,
        formTemplatesLoadOptions,
        onCreateTag,
        sectorsLoadOptions,
        sitesLoadOptions,
        tagsLoadOptions,
        tasksLoadOptions,
        openEquipmentModal,
        openAreasModal,
        openFormTemplatesModal,
        openTasksModal,
        openAddCategoryModal: canManageCategories
          ? openAddCategoryModal
          : undefined,
        categoryCacheKey,
        renewalReminderTemplate,
        hasPriorReminderValues,
        isFullAdmin,
        isPlanLocked,
        hasCategoriesAccess,
        hasSitesAccess,
        hasAssetsAccess,
        hasTasksAccess,
        hasUsersAccess,
        isAiEnabled,
        isVideoAiIndexingSupported,
        documentationVersionId,
        aiCredits,
        doc: data,
        isFieldDismissed: isAiFieldDismissed,
        onDismissField: dismissAiField,
      }),
    [
      t,
      isEdit,
      approverLoadOptions,
      areasLoadOptions,
      categoriesLoadOptions,
      equipmentLoadOptions,
      formTemplatesLoadOptions,
      onCreateTag,
      sectorsLoadOptions,
      sitesLoadOptions,
      tagsLoadOptions,
      tasksLoadOptions,
      openEquipmentModal,
      openAreasModal,
      openFormTemplatesModal,
      openTasksModal,
      canManageCategories,
      openAddCategoryModal,
      categoryCacheKey,
      renewalReminderTemplate,
      hasPriorReminderValues,
      isFullAdmin,
      isPlanLocked,
      hasCategoriesAccess,
      hasSitesAccess,
      hasAssetsAccess,
      hasTasksAccess,
      hasUsersAccess,
      isAiEnabled,
      isVideoAiIndexingSupported,
      documentationVersionId,
      aiCredits,
      data,
      isAiFieldDismissed,
      dismissAiField,
    ],
  );

  const defaultValues = useMemo(
    () => getDefaultValuesFromQueryData(data, prefillData, linkedEntityIds),
    [data, prefillData, linkedEntityIds],
  );

  const breadCrumbsData = useMemo(
    () => getBreadCrumbsDataByKey({ pathname, t, entity: data }),
    [pathname, t, data],
  );

  const title = isEdit ? t('edit-document') : t('create-document');

  const isEquipmentModalOpen = useModalManagementResult.isModalOpened(
    SELECT_EQUIPMENT_MODAL,
  );
  const isAreasModalOpen =
    useModalManagementResult.isModalOpened(SELECT_AREAS_MODAL);
  const isTasksModalOpen =
    useModalManagementResult.isModalOpened(SELECT_TASKS_MODAL);
  const isFormTemplatesModalOpen = useModalManagementResult.isModalOpened(
    SELECT_FORM_TEMPLATES_MODAL,
  );

  // Only include items the user has explicitly toggled in a prior modal
  // session — untouched rows must fall through to the modal's BE-baseline
  // `getInitialValue`, not get overridden by a default `true`.
  const buildVisibilityMapFromField = useCallback(
    (fieldName: string): Record<string, boolean> => {
      const list = formRef.current?.values?.[fieldName];
      if (!Array.isArray(list)) return {};
      return list.reduce<Record<string, boolean>>((acc, item) => {
        const id = String((item as { value?: string })?.value ?? '');
        if (!id) return acc;
        const value = (item as { visibleViaQr?: boolean })?.visibleViaQr;
        if (value === undefined) return acc;
        acc[id] = value;
        return acc;
      }, {});
    },
    [],
  );

  const equipmentInitialVisibilityMap = isEquipmentModalOpen
    ? buildVisibilityMapFromField(DocumentFormFieldNames.Equipment)
    : undefined;
  const areasInitialVisibilityMap = isAreasModalOpen
    ? buildVisibilityMapFromField(DocumentFormFieldNames.Areas)
    : undefined;

  return (
    <>
      <BreadCrumbs data={breadCrumbsData} />
      {firstLoading ? (
        <CircleLoader />
      ) : (
        <DashboardContentMaxWidth>
          <PageHeader title={title} />
          <PageBody>
            <WhiteBox>
              <AiFieldGenerationProvider value={aiGeneration}>
                <Form
                  actionsComponent={DocumentFormActions}
                  actionsComponentProps={{ onCancel, isEdit }}
                  defaultValues={defaultValues}
                  fields={formFields}
                  formRef={formRef}
                  graphQLErrors={graphQLErrors}
                  loading={loading}
                  onSubmit={onSubmitForm}
                  graphQLErrorsReplacePathMap={{
                    renewalRepetition:
                      DocumentFormFieldNames.RenewalReminderRepetition,
                  }}
                />
              </AiFieldGenerationProvider>
            </WhiteBox>
          </PageBody>
        </DashboardContentMaxWidth>
      )}
      <AssetsModalTableMultiselect
        isOpen={isEquipmentModalOpen}
        onClose={onCloseEquipmentModal}
        onSelect={onEquipmentSelect}
        formRef={formRef}
        fieldName={DocumentFormFieldNames.Equipment}
        documentationId={data?.id}
        // Free plan: BE enforces a single linked asset per document — cap
        // the picker selection at 1 so the user can't even queue an invalid
        // multi-asset selection client-side.
        selectionLimit={isPlanLocked ? 1 : undefined}
        // "Visible via QR" is a premium EDM column — drop the prop on a
        // plan-locked company so the column doesn't appear in the picker.
        visibleViaQr={
          isPlanLocked
            ? undefined
            : { initialVisibilityMap: equipmentInitialVisibilityMap }
        }
      />
      <SiteAreasModalTableMultiselect
        isOpen={isAreasModalOpen}
        onClose={onCloseAreasModal}
        onSelect={onAreasSelect}
        formRef={formRef}
        fieldName={DocumentFormFieldNames.Areas}
        documentationId={data?.id}
        visibleViaQr={
          isPlanLocked
            ? undefined
            : { initialVisibilityMap: areasInitialVisibilityMap }
        }
      />
      <TasksTableMultiselect
        isOpen={isTasksModalOpen}
        onClose={onCloseTasksModal}
        onSelect={onTasksSelect}
        formRef={formRef}
        fieldName={DocumentFormFieldNames.Tasks}
      />
      <FormTemplatesModalTableMultiselect
        isOpen={isFormTemplatesModalOpen}
        onClose={onCloseFormTemplatesModal}
        onSelect={onFormTemplatesSelect}
        formRef={formRef}
        fieldName={DocumentFormFieldNames.FormTemplates}
        documentationId={data?.id}
      />
      <Modal
        isOpen={isCategoryModalOpen}
        title={t('create-new-category')}
        onClose={closeAddCategoryModal}
      >
        <DocumentCategoryForm
          graphQLErrors={getGraphQLErrors(categoryCreateMutationResult.error)}
          loading={categoryCreateMutationResult.loading}
          onCancel={closeAddCategoryModal}
          onSubmit={onSubmitAddCategory}
        />
      </Modal>
    </>
  );
}
